import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Auth guard for student routes (main learner app).
 * Requires valid "student_token" in sessionStorage.
 * Validates token via /api/auth/validate endpoint.
 * Redirects to /login if missing or invalid.
 */
export function RequireStudentAuth({ children }) {
  const [isValid, setIsValid] = useState(null); // null = checking, true/false = result
  const location = useLocation();

  useEffect(() => {
    const validateToken = async () => {
      const token = sessionStorage.getItem("student_token");

      if (!token) {
        setIsValid(false);
        return;
      }

      try {
        const res = await fetch(`${API}/api/auth/validate`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.valid && data.role === "student") {
            setIsValid(true);
          } else {
            setIsValid(false);
          }
        } else {
          setIsValid(false);
        }
      } catch (err) {
        setIsValid(false);
      }
    };

    validateToken();
  }, [location.pathname]);

  // Still checking
  if (isValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-400 text-sm">Authenticating...</div>
      </div>
    );
  }

  // Invalid token — redirect to login
  if (isValid === false) {
    return <Navigate to="/login" replace />;
  }

  // Valid token — render protected content
  return children;
}

/**
 * Auth guard for teacher routes.
 * Requires valid "teacher_token" in sessionStorage.
 * Validates token via /api/auth/validate endpoint.
 * Redirects to /teacher/login if missing or invalid.
 */
export function RequireTeacherAuth({ children }) {
  const [isValid, setIsValid] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const validateToken = async () => {
      const token = sessionStorage.getItem("teacher_token");

      if (!token) {
        setIsValid(false);
        return;
      }

      try {
        const res = await fetch(`${API}/api/auth/validate`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.valid && data.role === "teacher") {
            setIsValid(true);
          } else {
            setIsValid(false);
          }
        } else {
          setIsValid(false);
        }
      } catch (err) {
        setIsValid(false);
      }
    };

    validateToken();
  }, [location.pathname]);

  // Still checking
  if (isValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-400 text-sm">Authenticating...</div>
      </div>
    );
  }

  // Invalid token — redirect to teacher login
  if (isValid === false) {
    return <Navigate to="/teacher/login" replace />;
  }

  // Valid token — render protected content
  return children;
}
